import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft,
  ShieldCheck,
  XCircle,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  usePermitDetail,
  useCancelPermit,
} from "@/hooks/use-permits";
import { PermitForm } from "@/components/permits/permit-form";
import { PermitStatusBadge } from "@/components/permits/permit-status-badge";
import { PermitCountdown } from "@/components/permits/permit-countdown";
import { PermitActivationSheet } from "@/components/permits/permit-activation-sheet";
import { PermitCloseoutSheet } from "@/components/permits/permit-closeout-sheet";
import { PermitSharedFields } from "@/components/permits/permit-shared-fields";
import { CloseOutChecklist } from "@/components/permits/close-out-checklist";
import { useGoBack } from "@/hooks/use-go-back";

export const Route = createFileRoute("/_authenticated/permits/$permitId")({
  component: PermitDetailPage,
});

const TYPE_LABELS: Record<string, string> = {
  hot_work: "Hot Work",
  confined_space: "Confined Space",
  excavation: "Excavation",
  fall_protection: "Fall Protection",
};

function PermitDetailPage() {
  const { permitId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = Route.useRouteContext() as {
    user: { id: string; role?: string | null };
  };

  const { data: permitData, isLoading, isError } = usePermitDetail(permitId);
  const cancelMutation = useCancelPermit(permitId);

  const [showActivationSheet, setShowActivationSheet] = useState(false);
  const [showCloseoutSheet, setShowCloseoutSheet] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const permit = permitData?.data;
  const canManagePermit = ["admin", "div_admin", "office_personnel"].includes(
    user?.role || ""
  );

  // Parse permit number from formDataJson
  let permitNumber = "";
  let formData: Record<string, unknown> = {};
  if (permit?.formDataJson) {
    try {
      formData = JSON.parse(permit.formDataJson);
      permitNumber = (formData._permitNumber as string) || "";
    } catch {
      // ignore
    }
  }

  // Parse close-out checklist for completed permits
  let closeOutValues: Record<string, boolean> = {};
  if (permit?.closeOutJson) {
    try {
      closeOutValues = JSON.parse(permit.closeOutJson);
    } catch {
      // ignore
    }
  }

  const handleActivated = () => {
    setShowActivationSheet(false);
    queryClient.invalidateQueries({ queryKey: ["permit", permitId] });
    queryClient.invalidateQueries({ queryKey: ["permits"] });
  };

  const handleClosed = () => {
    setShowCloseoutSheet(false);
    queryClient.invalidateQueries({ queryKey: ["permit", permitId] });
    queryClient.invalidateQueries({ queryKey: ["permits"] });
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error("Please provide a cancellation reason");
      return;
    }
    try {
      await cancelMutation.mutateAsync({ reason: cancelReason });
      toast.success("Permit cancelled");
      setShowCancelConfirm(false);
      setCancelReason("");
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Failed to cancel permit";
      toast.error(msg);
    }
  };

  const goBackOrigin = useGoBack(permit?.flhaId ? { to: "/flha/$flhaId", params: { flhaId: permit.flhaId } } : { to: "/permits" });
  const handleBack = goBackOrigin;

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-lg mx-auto">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !permit) {
    return (
      <div className="max-w-lg mx-auto">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm text-destructive">
              Failed to load permit. It may have been deleted.
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate({ to: "/permits" })}
            >
              Back to Permits
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const typeLabel = TYPE_LABELS[permit.permitType] || permit.permitType;

  return (
    <div className="space-y-4 max-w-lg mx-auto pb-24">
      {/* Header */}
      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={handleBack}
          className="p-1 rounded hover:bg-muted transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-text-primary" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-lg font-bold text-text-primary">
              {permitNumber || `${typeLabel} Permit`}
            </h1>
            <PermitStatusBadge status={permit.status} />
          </div>
          <p className="text-xs text-muted-foreground">
            {typeLabel} | Created{" "}
            {new Date(permit.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </p>
        </div>
      </div>

      {/* Active permit countdown */}
      {permit.status === "active" && permit.expiresAt && (
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="py-3 px-4 flex items-center justify-between">
            <span className="text-sm font-medium text-green-800">
              Time Remaining
            </span>
            <PermitCountdown expiresAt={permit.expiresAt} />
          </CardContent>
        </Card>
      )}

      {/* Activated banner */}
      {permit.status === "active" && permit.activatedAt && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
          <ShieldCheck className="h-5 w-5 text-green-600 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-800">
              Activated by supervisor
            </p>
            <p className="text-xs text-green-600">
              {new Date(permit.activatedAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </p>
          </div>
        </div>
      )}

      {/* Completed banner */}
      {permit.status === "completed" && permit.closedAt && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-blue-600 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-blue-800">
              Closed out
            </p>
            <p className="text-xs text-blue-600">
              {new Date(permit.closedAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </p>
          </div>
        </div>
      )}

      {/* Cancelled banner */}
      {permit.status === "cancelled" && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
          <p className="text-sm font-semibold text-orange-800">Cancelled</p>
          {permit.cancelReason && (
            <p className="text-xs text-orange-700 mt-1">
              Reason: {permit.cancelReason}
            </p>
          )}
          {permit.cancelledAt && (
            <p className="text-xs text-orange-600 mt-0.5">
              {new Date(permit.cancelledAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </p>
          )}
        </div>
      )}

      {/* Expired banner */}
      {permit.status === "expired" && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm font-semibold text-red-800">Expired</p>
          {permit.expiresAt && (
            <p className="text-xs text-red-600 mt-0.5">
              Expired at{" "}
              {new Date(permit.expiresAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </p>
          )}
        </div>
      )}

      {/* Draft: editable form */}
      {permit.status === "draft" && (
        <PermitForm
          permitType={permit.permitType}
          permitId={permit.id}
          flhaId={permit.flhaId || ""}
          highRiskInstanceId={permit.highRiskInstanceId || ""}
          projectId={permit.projectId}
        />
      )}

      {/* Active / Completed / Cancelled / Expired: read-only fields */}
      {permit.status !== "draft" && (
        <Card>
          <CardContent className="py-4 space-y-3">
            <h3 className="text-sm font-semibold">Permit Details</h3>
            <PermitSharedFields
              values={{
                workLocation: permit.workLocation || "",
                workDescription: permit.workDescription || "",
                startTime: permit.startTime || "",
                endTime: permit.endTime || "",
                workersInvolved: formatWorkers(permit.workersInvolvedJson),
                specialConditions: permit.specialConditions || "",
              }}
              onChange={() => {}}
              disabled
            />
          </CardContent>
        </Card>
      )}

      {/* Close-out checklist review (for completed permits) */}
      {permit.status === "completed" && permit.closeOutJson && (
        <Card>
          <CardContent className="py-4">
            <CloseOutChecklist
              permitType={permit.permitType}
              values={closeOutValues}
              onChange={() => {}}
              disabled
            />
          </CardContent>
        </Card>
      )}

      {/* Cancel confirmation */}
      {showCancelConfirm && (
        <Card className="border-orange-200">
          <CardContent className="py-4 space-y-3">
            <p className="text-sm font-medium">Cancel this permit?</p>
            <textarea
              className="w-full border rounded-md p-2 text-sm"
              placeholder="Cancellation reason (required)"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={2}
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCancelConfirm(false)}
              >
                Never mind
              </Button>
              <Button
                size="sm"
                className="bg-orange-600 hover:bg-orange-700 text-white gap-1"
                onClick={handleCancel}
                disabled={cancelMutation.isPending || !cancelReason.trim()}
              >
                {cancelMutation.isPending && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                )}
                Confirm Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action buttons */}
      {permit.status === "draft" && canManagePermit && (
        <Button
          className="w-full bg-green-600 hover:bg-green-700 text-white gap-2"
          onClick={() => setShowActivationSheet(true)}
        >
          <ShieldCheck className="h-4 w-4" />
          Activate Permit
        </Button>
      )}

      {permit.status === "active" && canManagePermit && (
        <Button
          className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2"
          onClick={() => setShowCloseoutSheet(true)}
        >
          <CheckCircle2 className="h-4 w-4" />
          Close Out Permit
        </Button>
      )}

      {(permit.status === "draft" || permit.status === "active") &&
        !showCancelConfirm && (
          <Button
            variant="outline"
            className="w-full border-orange-300 text-orange-700 hover:bg-orange-50 gap-2"
            onClick={() => setShowCancelConfirm(true)}
          >
            <XCircle className="h-4 w-4" />
            Cancel Permit
          </Button>
        )}

      {/* Sheets */}
      <PermitActivationSheet
        isOpen={showActivationSheet}
        onClose={() => setShowActivationSheet(false)}
        permitId={permitId}
        onActivated={handleActivated}
      />

      <PermitCloseoutSheet
        isOpen={showCloseoutSheet}
        onClose={() => setShowCloseoutSheet(false)}
        permitId={permitId}
        permitType={permit.permitType}
        onClosed={handleClosed}
      />
    </div>
  );
}

function formatWorkers(json: string | null): string {
  if (!json) return "";
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) {
      return parsed.map((w: { name: string }) => w.name).join(", ");
    }
    return String(parsed);
  } catch {
    return json;
  }
}
